import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
    ConfigurationObject,
    RequirementsDto,
    RequirementsListItem,
    RequirementsService,
} from 'app/api';
import { UserService } from 'app/core/user/user.service';
import {
    BehaviorSubject,
    Observable,
    Subject,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { ConfigurationsService } from '../configurations/configurations.service';
import { MailCategory, MailFilter, MailFolder, MailLabel } from './mailbox.types';

@Injectable({ providedIn: 'root' })
export class RequirementsPageService implements OnDestroy {
    private _destroy: Subject<void> = new Subject();
    selectedMailChanged: BehaviorSubject<any> = new BehaviorSubject(null);
    private _category: BehaviorSubject<MailCategory> = new BehaviorSubject(null);
    private _filters: BehaviorSubject<MailFilter[]> = new BehaviorSubject(null);
    private _folders: BehaviorSubject<MailFolder[]> = new BehaviorSubject(null);
    private _labels: BehaviorSubject<MailLabel[]> = new BehaviorSubject(null);
    private _mails: BehaviorSubject<Array<RequirementsListItem>> = new BehaviorSubject(null);
    private _mailsLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _mail: BehaviorSubject<RequirementsDto> = new BehaviorSubject(null);

    lastUsedMethod: { function: any; params: any[] };

    listFilter: {
        status: string;
        systemId: string;
        areaId: string;
        sortBy: string;
        sortDir: string;
    } = {
        status: null,
        systemId: null,
        areaId: null,
        sortBy: null,
        sortDir: null,
    };

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private apiService: RequirementsService,
        private configurationService: ConfigurationsService,
        private _userService: UserService
    ) {}
    ngOnDestroy(): void {
        this._destroy.next();
        this._destroy.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for category
     */
    get category$(): Observable<MailCategory> {
        return this._category.asObservable();
    }

    /**
     * Getter for filters
     */
    get filters$(): Observable<MailFilter[]> {
        return this._filters.asObservable();
    }

    /**
     * Getter for folders
     */
    get folders$(): Observable<MailFolder[]> {
        return this._folders.asObservable();
    }

    /**
     * Getter for labels
     */
    get labels$(): Observable<MailLabel[]> {
        return this._labels.asObservable();
    }

    /**
     * Getter for mails
     */
    get mails$(): Observable<Array<RequirementsListItem>> {
        return this._mails.asObservable();
    }

    /**
     * Getter for mails loading
     */
    get mailsLoading$(): Observable<boolean> {
        return this._mailsLoading.asObservable();
    }

    /**
     * Getter for mail
     */
    get mail$(): Observable<RequirementsDto> {
        return this._mail.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get filters
     */
    getFilters(): Observable<any> {
        const filters: MailFilter[] = [
            {
                id: 'de1b41f6-6839-4f1b-9d2c-07e55f6f8f82',
                title: 'Starred',
                slug: 'starred',
                icon: 'heroicons_outline:star',
            },
            {
                id: '71bba1ec-a90e-4a71-9932-4bab0a99aa1c',
                title: 'Important',
                slug: 'important',
                icon: 'heroicons_outline:exclamation-circle',
            },
        ];
        this._filters.next(filters);
        return of(filters);
    }

    /**
     * Get folders
     */
    getFolders(): Observable<any> {
        const folders: MailFilter[] = [
            {
                id: '7c004a19-4506-48ef-93ab-f16381302e3b',
                title: 'Inbox',
                slug: 'inbox',
                icon: 'heroicons_outline:inbox',
            },
            {
                id: 'fbdc8e79-a0c4-4a27-bc98-9c81ee7a86e5',
                title: 'My requirements',
                slug: 'my_requirements',
                icon: 'heroicons_outline:document',
            },
        ];

        this._folders.next(folders);
        return of(folders);
    }

    /**
     * Get labels
     */
    getLabels(): Observable<any> {
        return this.configurationService.getConfigurationByTypeObservable('labels').pipe(
            tap((response: ConfigurationObject) => {
                this._labels.next(response.value[response.configType] as any[]);
                return response;
            }),
            switchMap(response => {
                if (response === null) {
                    return throwError({
                        message: 'Labels are not available!',
                    });
                }

                return of(response);
            })
        );
    }

    /**
     * Get mails by filter
     */
    getMailsByFilter(filter: string): Observable<any> {
        // Execute the mails loading with true
        this._mailsLoading.next(true);

        const filters = this._filters.getValue();
        const filterName = filters.find(f => f.slug === filter)?.title;

        this._category.next({
            type: 'filter',
            name: filterName,
        });

        const starred = filter === 'starred' ? true : undefined;
        const important = filter === 'important' ? true : undefined;
        const unread = filter === 'unread' ? true : undefined;
        const authorName = undefined;

        return this.apiService
            .getAllRequirements(
                starred,
                important,
                unread,
                authorName,
                undefined,
                this.listFilter.status,
                this.listFilter.areaId,
                this.listFilter.systemId,
                this.listFilter.sortBy,
                this.listFilter.sortDir
            )
            .pipe(
                tap((response: Array<RequirementsListItem>) => {
                    this._mails.next(response);
                    this._mailsLoading.next(false);

                    this.lastUsedMethod = {
                        function: this.getMailsByFilter.bind(this),
                        params: [filter],
                    };
                }),
                switchMap(response => {
                    if (response === null) {
                        return throwError({
                            message: 'Requested page is not available!',
                        });
                    }

                    return of(response);
                })
            );
    }

    getRequirements(
        areaId: string,
        systemId: string,
        status: string
    ): Observable<Array<RequirementsListItem>> {
        return this.apiService.getAllRequirements(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            status,
            areaId,
            systemId,
            this.listFilter.sortBy,
            this.listFilter.sortDir
        );
    }

    /**
     * Get mails by folder
     */
    getMailsByFolder(folder: string): Observable<any> {
        // Execute the mails loading with true
        this._mailsLoading.next(true);

        const folders = this._folders.getValue();
        const folderName = folders.find(f => f.slug === folder)?.title;

        this._category.next({
            type: 'filter',
            name: folderName,
        });

        this._mails.next([]);
        this._mailsLoading.next(false);

        let authorName = undefined;
        if (folder === 'my_requirements') {
            authorName = this._userService.user.firstname + ' ' + this._userService.user.lastname;
        }

        return this.apiService
            .getAllRequirements(
                undefined,
                undefined,
                undefined,
                authorName,
                undefined,
                this.listFilter.status,
                this.listFilter.areaId,
                this.listFilter.systemId,
                this.listFilter.sortBy,
                this.listFilter.sortDir
            )
            .pipe(
                tap((response: Array<RequirementsDto>) => {
                    this._mails.next(response);
                    this._mailsLoading.next(false);

                    this.lastUsedMethod = {
                        function: this.getMailsByFolder.bind(this),
                        params: [folder],
                    };
                }),
                switchMap(response => {
                    if (response === null) {
                        return throwError({
                            message: 'Requested page is not available!',
                        });
                    }

                    return of(response);
                })
            );
    }

    /**
     * Get mails by label
     */
    getMailsByLabel(label: string): Observable<any> {
        // Execute the mails loading with true
        this._mailsLoading.next(true);

        const labels = this._labels.getValue();
        const labelName = labels.find(f => f.id === label)?.text;

        this._category.next({
            type: 'filter',
            name: labelName,
        });

        return this.apiService
            .getAllRequirements(
                undefined,
                undefined,
                undefined,
                undefined,
                label,
                this.listFilter.status,
                this.listFilter.areaId,
                this.listFilter.systemId,
                this.listFilter.sortBy,
                this.listFilter.sortDir
            )
            .pipe(
                tap((response: Array<RequirementsListItem>) => {
                    this._mails.next(response);

                    this.lastUsedMethod = {
                        function: this.getMailsByLabel.bind(this),
                        params: [label],
                    };

                    this._mailsLoading.next(false);
                }),
                switchMap(response => {
                    if (response === null) {
                        return throwError({
                            message: 'Requested page is not available!',
                        });
                    }

                    return of(response);
                })
            );
    }

    /**
     * Get mail by id
     */
    getMailById(id: string): Observable<any> {
        return this.apiService.getRequirement(id).pipe(
            map((response: RequirementsDto) => {
                this._mail.next(response);

                return response;
            }),
            switchMap(response => {
                if (!response) {
                    return throwError('Could not found mail with id of ' + id + '!');
                }

                return of(response);
            })
        );
    }

    /**
     * Update mail
     *
     * @param id
     * @param mail
     */
    updateMail(id: string, mail: RequirementsDto): Observable<any> {
        return this.apiService.updateRequirement(id, mail).pipe(
            tap(() => {
                // Re-fetch the folders on mail update
                // to get the updated counts on the sidebar
                this.lastUsedMethod.function(this.lastUsedMethod.params[0]).subscribe();
            })
        );
    }

    removeRequirement(id: string): Observable<any> {
        return this.apiService.deleteRequirement(id);
    }

    /**
     * Reset the current mail
     */
    resetMail(): Observable<boolean> {
        return of(true).pipe(
            take(1),
            tap(() => {
                this._mail.next(null);
            })
        );
    }

    /**
     * Add label
     *
     * @param label
     */
    addLabel(label: MailLabel): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels =>
                this._httpClient.post<MailLabel>('api/apps/mailbox/label', { label }).pipe(
                    map(newLabel => {
                        // Update the labels with the new label
                        this._labels.next([...labels, newLabel]);

                        // Return the new label
                        return newLabel;
                    })
                )
            )
        );
    }

    /**
     * Update label
     *
     * @param id
     * @param label
     */
    updateLabel(id: string, label: MailLabel): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels =>
                this._httpClient
                    .patch<MailLabel>('api/apps/mailbox/label', {
                        id,
                        label,
                    })
                    .pipe(
                        map((updatedLabel: any) => {
                            // Find the index of the updated label within the labels
                            const index = labels.findIndex(item => item.id === id);

                            // Update the label
                            labels[index] = updatedLabel;

                            // Update the labels
                            this._labels.next(labels);

                            // Return the updated label
                            return updatedLabel;
                        })
                    )
            )
        );
    }

    /**
     * Delete label
     *
     * @param id
     */
    deleteLabel(id: string): Observable<any> {
        return this.labels$.pipe(
            take(1),
            switchMap(labels =>
                this._httpClient.delete('api/apps/mailbox/label', { params: { id } }).pipe(
                    map((isDeleted: any) => {
                        // Find the index of the deleted label within the labels
                        const index = labels.findIndex(item => item.id === id);

                        // Delete the label
                        labels.splice(index, 1);

                        // Update the labels
                        this._labels.next(labels);

                        // Return the deleted status
                        return isDeleted;
                    })
                )
            )
        );
    }

    sendRequirement(requirement: RequirementsDto): Observable<any> {
        return this.apiService.createRequirement(requirement);
    }
}
